import { useState, type ReactNode } from "react";
import type {
  BuyerDealStructureDto,
  BuyerDealStructureStatus,
  BuyerSubmissionDetailDto,
} from "@/api/buyers";
import {
  Button,
  EmptyState,
  ResponsiveGrid,
  StatusBadge,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { useBuyerDealStructure } from "@/hooks/buyers/useBuyerDealStructure";
import { useDateFormatter } from "@/utils/datetime";
import { DetailField, DetailFieldList, DetailSection } from "../detailBits";
import { QueryErrorState } from "../queryStates";
import { SubmissionNotesCard } from "./SubmissionNotesCard";
import { DealStructureEditModal } from "./dealStructure/DealStructureEditModal";
import { DealStructureWizard } from "./dealStructure/DealStructureWizard";
import {
  BUILD_BUTTON,
  EDIT_BUTTON,
  PRICE_BASIS_LABELS,
  STATUS_LABELS,
  STRUCTURE_CARD,
  SUGGESTED_CARD,
  annualCashFlowLabel,
  multipleOf,
  suggestedCashFlowLabel,
  suggestedDownPaymentLabel,
} from "./dealStructure/dealStructureCopy";

/**
 * The Deal structure tab — Phoenix's Potential Deal Structure (package
 * document 3), the deterministic Suggested structure beside it, and the two
 * ways to set it: BUILD THE DEAL STRUCTURE (the wizard) and EDIT DEAL
 * STRUCTURE (every field at once, once it is complete).
 *
 * <p>🔴 <b>No AI anywhere on this tab.</b> The Suggested card is read from the
 * listing and the buyer's prequalification or assessment, and there is no
 * PREPARE control: the structure is Phoenix's illustration, built by staff.</p>
 *
 * <p><b>Container-driven layout.</b> `ResponsiveGrid` reflows on the width of
 * its box, not the viewport's — the tab renders inside the ~420px record
 * drawer as well as on the full page.</p>
 */
export function SubmissionDealStructureTab({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const query = useBuyerDealStructure(s.id);
  const dto = query.data;
  const writable = canWrite && Boolean(dto?.canEdit);

  return (
    <div className="grid gap-space-5">
      {query.isError ? (
        <QueryErrorState
          what="the deal structure"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : (
        <ResponsiveGrid cols={2} minColWidth="22rem" gap="lg">
          <DetailSection
            title={STRUCTURE_CARD.title}
            action={
              writable && dto ? (
                // Wraps and right-aligns: at drawer width the two buttons are
                // wider than the card, so they stack inside the action slot.
                <div className="flex flex-wrap items-center justify-end gap-space-2">
                  <Button
                    size="sm"
                    data-testid="deal-structure-build"
                    onClick={() => setWizardOpen(true)}
                  >
                    {BUILD_BUTTON}
                  </Button>
                  {/* Only on a complete structure: its save confirms the whole
                      structure, so it is not a way round the wizard's steps. */}
                  {dto.status === "Complete" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid="deal-structure-edit"
                      onClick={() => setEditing(true)}
                    >
                      {EDIT_BUTTON}
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          >
            {dto ? <StructureBody dto={dto} /> : <Loading />}
          </DetailSection>

          <DetailSection title={SUGGESTED_CARD.title}>
            {dto ? <SuggestedBody dto={dto} /> : <Loading />}
          </DetailSection>
        </ResponsiveGrid>
      )}

      <SubmissionNotesCard s={s} canWrite={canWrite} />

      {/* Mounted at the top level of the tab body: the shared Modal portals to
          document.body, and an overlay opened from inside collapsible content
          paints nothing while still locking the page's scroll. */}
      <DealStructureWizard
        submissionId={s.id}
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />
      {dto && (
        <DealStructureEditModal
          submissionId={s.id}
          dto={dto}
          open={editing}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

const STATUS_TONES: Record<BuyerDealStructureStatus, StatusTone> = {
  NotBuilt: "muted",
  InProgress: "progress",
  Complete: "ok",
};

function StatusLine({ status }: { status: BuyerDealStructureStatus }) {
  return (
    <div className="mb-space-3">
      <StatusBadge tone={STATUS_TONES[status] ?? "muted"} data-testid="deal-structure-status">
        {STATUS_LABELS[status] ?? status}
      </StatusBadge>
    </div>
  );
}

/** The saved structure. Every figure is the server's. */
function StructureBody({ dto }: { dto: BuyerDealStructureDto }) {
  const f = dto.figures;
  if (dto.status === "NotBuilt") {
    return (
      <>
        <StatusLine status={dto.status} />
        <EmptyState
          title={STRUCTURE_CARD.emptyTitle}
          description={STRUCTURE_CARD.emptyBody}
          className="py-space-5"
        />
      </>
    );
  }
  const sde = dto.cashFlowType === "Sde";
  const cashFlow = sde ? dto.freeCashFlowEstimate : dto.cashFlow;
  return (
    <>
      <StatusLine status={dto.status} />
      <DetailFieldList data-testid="deal-structure-rows">
        <DetailField label={STRUCTURE_CARD.rows.price} value={money(f.price)} />
        <DetailField
          label={STRUCTURE_CARD.rows.priceBasis}
          value={dto.priceBasis ? PRICE_BASIS_LABELS[dto.priceBasis] : null}
        />
        <DetailField label={STRUCTURE_CARD.rows.downPayment} value={money(f.downPayment)} />
        <DetailField label={STRUCTURE_CARD.rows.financed} value={money(f.financed)} />
        {f.tenYear != null && (
          <DetailField label={STRUCTURE_CARD.rows.financedTenYear} value={money(f.tenYear.financed)} />
        )}
        <DetailField label={annualCashFlowLabel(dto.cashFlowType)} value={money(cashFlow)} />
        <DetailField label={STRUCTURE_CARD.rows.financedMultiple} value={multipleOf(f.financedMultiple) || null} />
        {f.balance != null && f.balance > 0 && (
          <DetailField label={STRUCTURE_CARD.rows.balance} value={money(f.balance)} />
        )}
        {f.tenYear != null && f.tenYear.balance > 0 && (
          <DetailField label={STRUCTURE_CARD.rows.balanceTenYear} value={money(f.tenYear.balance)} />
        )}
        <DetailField label={STRUCTURE_CARD.rows.note} value={dto.note || null} wide />
      </DetailFieldList>
    </>
  );
}

/** The deterministic Suggested structure (D20, ruling Q2), each row with its source. */
function SuggestedBody({ dto }: { dto: BuyerDealStructureDto }) {
  const { date } = useDateFormatter();
  const s = dto.suggested;
  const unavailable = s.supportedPriceUnavailableReason
    ? SUGGESTED_CARD.unavailable[s.supportedPriceUnavailableReason]
    : null;
  return (
    <div className="grid gap-space-3">
      <p className="text-small text-gray-mid m-0">{SUGGESTED_CARD.body}</p>
      <DetailFieldList data-testid="deal-structure-suggested">
        <DetailField
          label={SUGGESTED_CARD.askingPrice}
          value={
            s.askingPrice == null ? null : (
              <Sourced
                value={formatCurrency(s.askingPrice)}
                source={s.askingPriceFromSubmission
                  ? SUGGESTED_CARD.askingFromSubmission
                  : SUGGESTED_CARD.askingFromListing}
              />
            )
          }
        />
        <DetailField
          label={suggestedCashFlowLabel(s.listingCashFlowType)}
          value={
            s.listingCashFlow == null ? null : (
              <Sourced value={formatCurrency(s.listingCashFlow)} source={SUGGESTED_CARD.cashFlowFromListing} />
            )
          }
        />
        <DetailField
          label={suggestedDownPaymentLabel(s.downPaymentSource)}
          value={
            s.downPayment == null ? null : (
              <Sourced
                value={formatCurrency(s.downPayment)}
                source={s.downPaymentSourceDate ? date(s.downPaymentSourceDate) : null}
              />
            )
          }
        />
        <DetailField
          label={SUGGESTED_CARD.supported}
          data-testid="deal-structure-suggested-supported"
          value={
            unavailable ? (
              <span className="text-gray-mid">{unavailable}</span>
            ) : s.supportedPrice == null ? null : (
              <Sourced
                value={formatCurrency(s.supportedPrice)}
                source={s.supportedPriceCapped ? SUGGESTED_CARD.supportedCapped : SUGGESTED_CARD.supportedSource}
              />
            )
          }
        />
      </DetailFieldList>
    </div>
  );
}

function Sourced({ value, source }: { value: string; source: string | null }) {
  return (
    <span>
      {value}
      {source && <span className="text-gray-mid">{` (${source})`}</span>}
    </span>
  );
}

function Loading(): ReactNode {
  return <p className="text-small text-gray-mid m-0">Loading…</p>;
}

function money(value: number | null | undefined): string | null {
  return value == null ? null : formatCurrency(value);
}
